import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, CircleCheck, ArrowRight, FileDown } from "lucide-react";
import Reveal, { Eyebrow } from "@/components/Reveal";
import ProductVisual from "@/components/ProductVisual";
import { EtsyButton, AmazonButton } from "@/components/BuyButtons";
import api from "@/lib/api";
import { FALLBACK_JOURNAL, MENS_JOURNAL, PURSE_EDITION } from "@/data/site";

function useJournal() {
  const [journal, setJournal] = useState(FALLBACK_JOURNAL);
  useEffect(() => {
    let alive = true;
    api
      .get("/products/in-your-corner-journal")
      .then((res) => {
        if (alive && res.data?.slug) setJournal({ ...FALLBACK_JOURNAL, ...res.data });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  return journal;
}

export default function Shop() {
  const journal = useJournal();

  return (
    <main data-testid="page-shop" className="pt-32 sm:pt-40 lg:pt-44">
      <section className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <ProductVisual image={journal.cover_image || "/journal-cover.jpg"} chips={false} className="mx-auto max-w-md" />
          </Reveal>
          <div>
            <Reveal>
              <Eyebrow>The first product</Eyebrow>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="mt-6 font-display text-4xl font-semibold tracking-tight text-ink sm:text-5xl" data-testid="shop-product-name">
                {journal.name}
              </h1>
            </Reveal>
            <Reveal delay={0.08}>
              <p className="mt-3 font-display text-xl italic text-lav-deep" data-testid="shop-product-tagline">
                {journal.tagline}
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-5 text-base leading-relaxed text-ink-soft sm:text-lg" data-testid="shop-product-description">
                {journal.description}
              </p>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="mt-7 flex flex-wrap items-center gap-2" data-testid="shop-product-badges">
                {["Fillable PDF", "Printable", "Any device", "Instant download"].map((chip) => (
                  <span key={chip} className="rounded-full bg-lav-soft px-4 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-lav-deep">
                    {chip}
                  </span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.14}>
              <div className="mt-7 flex items-end gap-3" data-testid="shop-price">
                <span className="font-display text-5xl font-semibold text-ink">{journal.price_label}</span>
                <span className="pb-2 text-sm text-ink-faint">{journal.price_note}</span>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-2.5 flex items-end gap-3" data-testid="shop-price-printed">
                <span className="font-display text-2xl font-semibold text-ink">{journal.printed_price_label}</span>
                <span className="pb-1 text-sm text-ink-faint">{journal.printed_price_note}</span>
              </div>
            </Reveal>
            <Reveal delay={0.16}>
              <div className="mt-7 flex flex-wrap items-center gap-4">
                <EtsyButton href={journal.buy_etsy_url} testId="shop-buy-etsy" />
                <AmazonButton href={journal.buy_amazon_url} testId="shop-buy-amazon" />
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-5 flex items-center gap-2 text-sm text-ink-faint" data-testid="shop-purchase-note">
                <FileDown className="h-4 w-4" />
                The digital PDF downloads instantly from Etsy. The printed edition ships from Amazon.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-6 px-5 sm:px-8 lg:grid-cols-3">
          <Reveal className="h-full">
            <article data-testid="shop-who-for" className="h-full rounded-3xl bg-lav-soft p-8 sm:p-10">
              <h2 className="font-display text-2xl font-semibold text-lav-deep">Who it's for</h2>
              <ul className="mt-6 space-y-3.5">
                {journal.who_for.map((item) => (
                  <li key={item} className="flex gap-3 text-sm leading-relaxed text-ink sm:text-base">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-lav-deep" />
                    {item}
                  </li>
                ))}
              </ul>
            </article>
          </Reveal>
          <Reveal delay={0.07} className="h-full">
            <article data-testid="shop-helps-with" className="h-full rounded-3xl border border-ink/5 bg-white p-8 sm:p-10">
              <h2 className="font-display text-2xl font-semibold text-ink">What it can help with</h2>
              <ul className="mt-6 grid gap-3">
                {journal.helps_with.map((item) => (
                  <li key={item} className="flex gap-3 text-sm leading-relaxed text-ink-soft sm:text-base">
                    <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-mint-deep" />
                    {item}
                  </li>
                ))}
              </ul>
            </article>
          </Reveal>
          <Reveal delay={0.14} className="h-full">
            <article data-testid="shop-format" className="h-full rounded-3xl bg-mint-soft p-8 sm:p-10">
              <h2 className="font-display text-2xl font-semibold text-mint-deep">Format</h2>
              <ul className="mt-6 space-y-3.5">
                {journal.format_notes.map((item) => (
                  <li key={item} className="flex gap-3 text-sm leading-relaxed text-ink sm:text-base">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-mint-deep" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-6 rounded-2xl bg-white/70 p-4 text-xs leading-relaxed text-ink-soft">
                In Your Corner is a personal healthcare organization tool—not medical advice and not a
                replacement for your doctor or professional care.
              </p>
            </article>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <h2 className="font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl" data-testid="editions-headline">
                One mission, three editions
              </h2>
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-blush-deep">All available now</p>
            </div>
          </Reveal>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:max-w-4xl">
            <Reveal className="h-full">
              <article data-testid="edition-original" className="h-full rounded-3xl border border-ink/5 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-lav-soft/70 sm:p-8">
                <div className="overflow-hidden rounded-2xl shadow-lg shadow-lav-soft/60">
                  <img
                    src="/journal-cover.png"
                    alt="In Your Corner Healthcare Journal — original edition cover"
                    className="block h-auto w-full object-contain"
                    loading="lazy"
                  />
                </div>
                <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-display text-xl font-semibold text-ink">Original Edition</h3>
                  <span className="rounded-full bg-mint-soft px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-mint-deep">
                    Available now
                  </span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-ink-soft">
                  Digital & printed. Instant download on Etsy, professionally printed by Amazon.
                </p>
              </article>
            </Reveal>
            <Reveal delay={0.07} className="h-full">
              <article data-testid="edition-masculine" className="h-full rounded-3xl border border-ink/5 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-lav-soft/70 sm:p-8">
                <div className="overflow-hidden rounded-2xl shadow-lg shadow-lav-soft/60">
                  <img
                    src={MENS_JOURNAL.cover_image}
                    alt="In Your Corner Healthcare Mini Patient Healthcare Journal — Men's Edition"
                    className="block h-auto w-full object-contain"
                    loading="lazy"
                  />
                </div>
                <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-display text-xl font-semibold text-ink">Men's Edition</h3>
                  <span className="rounded-full bg-mint-soft px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-mint-deep">
                    Available now
                  </span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-ink-soft">
                  {MENS_JOURNAL.tagline} Printable & fillable PDF, 14 pages—instant download on Etsy.
                </p>
                <div className="mt-4 flex items-end gap-2" data-testid="mens-price">
                  <span className="font-display text-2xl font-semibold text-ink">{MENS_JOURNAL.price_label}</span>
                  <span className="pb-0.5 text-xs text-ink-faint">{MENS_JOURNAL.price_note}</span>
                </div>
                <div className="mt-4">
                  <EtsyButton href={MENS_JOURNAL.buy_etsy_url} testId="mens-buy-etsy" className="!px-6 !py-3 text-sm" />
                </div>
              </article>
            </Reveal>
            <Reveal delay={0.14} className="h-full">
              <article data-testid="edition-purse" className="h-full rounded-3xl border border-ink/5 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-lav-soft/70 sm:p-8">
                <div className="overflow-hidden rounded-2xl shadow-lg shadow-lav-soft/60">
                  <img
                    src={PURSE_EDITION.cover_image}
                    alt="In Your Corner Healthcare Journal — 6×9 Purse Edition"
                    className="block h-auto w-full object-contain"
                    loading="lazy"
                  />
                </div>
                <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-display text-xl font-semibold text-ink">Purse Edition (6×9)</h3>
                  <span className="rounded-full bg-mint-soft px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-mint-deep">
                    Available now
                  </span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-ink-soft">
                  {PURSE_EDITION.tagline} Printed and shipped by Amazon—take it to every appointment.
                </p>
                <div className="mt-4 flex items-end gap-2" data-testid="purse-price">
                  <span className="font-display text-2xl font-semibold text-ink">{PURSE_EDITION.price_label}</span>
                  <span className="pb-0.5 text-xs text-ink-faint">{PURSE_EDITION.price_note}</span>
                </div>
                <div className="mt-4">
                  <AmazonButton href={PURSE_EDITION.buy_amazon_url} testId="purse-buy-amazon" className="!px-6 !py-3 text-sm" />
                </div>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <h2 className="font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl" data-testid="whats-inside-headline">
                What's inside the journal
              </h2>
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-blush-deep" data-testid="whats-inside-meta">
                14 pages · fillable · printable
              </p>
            </div>
          </Reveal>
          <div className="mt-8 grid items-center gap-10 lg:grid-cols-2">
            <Reveal>
              <div className="overflow-hidden rounded-3xl shadow-xl shadow-lav-soft/60 ring-1 ring-ink/5">
                <img
                  src="/journal-pages.png"
                  alt="Inside the Mini Patient Healthcare Journal—pages for your healthcare team, medications, appointments, symptom tracking, care plan, contacts, and notes"
                  className="block h-auto w-full object-contain"
                  loading="lazy"
                />
              </div>
            </Reveal>
            <div>
              <Reveal delay={0.08}>
                <ul className="grid gap-3 sm:grid-cols-2" data-testid="whats-inside-pages">
                  {(journal.pages || []).map((p) => (
                    <li
                      key={p}
                      className="flex items-center gap-3 rounded-2xl border border-ink/5 bg-white px-5 py-4 text-sm font-semibold text-ink transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-lav-soft/60"
                    >
                      <CircleCheck className="h-4 w-4 shrink-0 text-mint-deep" />
                      {p}
                    </li>
                  ))}
                </ul>
              </Reveal>
              <Reveal delay={0.14}>
                <p className="mt-6 flex items-start gap-2 rounded-2xl bg-blush-soft/70 p-5 text-sm leading-relaxed text-ink-soft" data-testid="physical-edition-note">
                  <FileDown className="mt-0.5 h-4 w-4 shrink-0 text-blush-deep" />
                  <span>
                    The digital edition is a fillable, printable PDF—instant download from Etsy. Prefer paper?
                    The 30-page printed journal ships from Amazon.
                  </span>
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
              <div>
                <Reveal>
                  <Eyebrow>Type it or print it — your choice</Eyebrow>
                </Reveal>
                <Reveal delay={0.05}>
                  <h2
                    className="mt-6 font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl"
                    data-testid="ways-headline"
                  >
                    Same journal. Two ways. Total flexibility.
                  </h2>
                </Reveal>
                <Reveal delay={0.1}>
                  <p className="mt-5 max-w-lg text-base leading-relaxed text-ink-soft sm:text-lg" data-testid="ways-sub">
                    Every In Your Corner Healthcare journal comes as a fillable PDF—type directly into the pages
                    on your computer, tablet, or phone and update anytime—or print it at home and write by hand.
                    Same journal, whichever way feels right for you.
                  </p>
                </Reveal>
                <Reveal delay={0.14}>
                  <ul className="mt-6 space-y-2.5 text-sm font-semibold text-ink sm:text-base">
                    <li className="flex items-center gap-3" data-testid="ways-point-1">
                      <CircleCheck className="h-4 w-4 shrink-0 text-mint-deep" /> Type directly in the pages
                    </li>
                    <li className="flex items-center gap-3" data-testid="ways-point-2">
                      <CircleCheck className="h-4 w-4 shrink-0 text-mint-deep" /> Print at home, if you prefer
                    </li>
                    <li className="flex items-center gap-3" data-testid="ways-point-3">
                      <CircleCheck className="h-4 w-4 shrink-0 text-mint-deep" /> Update anytime as things change
                    </li>
                    <li className="flex items-center gap-3" data-testid="ways-point-4">
                      <CircleCheck className="h-4 w-4 shrink-0 text-mint-deep" /> Use on any device
                    </li>
                  </ul>
                </Reveal>
              </div>
              <Reveal delay={0.08}>
                <div className="overflow-hidden rounded-3xl shadow-xl shadow-lav-soft/60 ring-1 ring-ink/5">
                  <img
                    src="/journal-ways.png"
                    alt="Type it or print it—the fillable digital version on a laptop or the printable version written by hand"
                    className="block h-auto w-full object-contain"
                    loading="lazy"
                  />
                </div>
              </Reveal>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 lg:pb-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="flex flex-col items-center justify-between gap-6 rounded-[2.5rem] bg-ink px-6 py-12 text-center sm:px-12 lg:flex-row lg:text-left">
              <div>
                <h2 className="font-display text-2xl font-semibold text-cream sm:text-3xl" data-testid="collection-band-title">
                  More editions are on the way
                </h2>
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-cream/70 sm:text-base">
                  A full-length organizer, a word search activity book, a caregiver edition for children's care,
                  and more—take a look at what's coming in the collection.
                </p>
              </div>
              <Link
                to="/collection"
                data-testid="collection-band-link"
                className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-cream px-8 py-4 font-bold text-ink transition-all duration-300 hover:bg-blush-soft hover:text-blush-deep"
              >
                View the Collection
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
